import React, { useState } from 'react';
import SEO from '../components/ui/SEO';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../components/ui/Button';
export default function FAQ() {
  const [openItem, setOpenItem] = useState<string | null>('instructing');
  const toggleItem = (id: string) => {
    if (openItem === id) {
      setOpenItem(null);
    } else {
      setOpenItem(id);
    }
  };
  const faqCategories = [{
    id: 'instructing',
    title: 'Instructing Dr. Imtiaz Syed',
    items: [{
      question: 'How do I instruct Dr. Syed for a medico-legal report or assessment?',
      answer: 'The most efficient way to instruct Dr. Syed is to send an initial enquiry via email to enquiries@medicolegal-psychiatry.co.uk. Please provide a brief outline of the case, the type of assessment required, and any urgent deadlines. Our team will then guide you through the instruction process, including providing terms and conditions and a service agreement.'
    }, {
      question: 'What information is typically required to begin an assessment?',
      answer: 'To facilitate a thorough and timely assessment, we typically require: Letter of Instruction (clearly outlining questions to be addressed), Relevant medical records (GP notes, hospital records, psychiatric reports), Legal documents pertinent to the case (e.g., statements, court orders), Any previous expert reports, and Details for contacting the examinee. A comprehensive list will be provided upon initial enquiry.'
    }]
  }, {
    id: 'reports',
    title: 'Reports & Expert Witness Testimony',
    items: [{
      question: 'What is the typical turnaround time for a medico-legal report?',
      answer: 'Standard report turnaround is 5–7 working days. For urgent enquiries, Dr Syed aims to respond within 1–2 days; this is a response to the enquiry, not a report delivery time. Report timing depends on the case, Dr Syed’s availability and receipt of complete instructions and records, and is confirmed before an instruction is accepted. Please mention any hearing or filing deadlines in your first email.'
    }, {
      question: 'Does Dr. Syed provide expert witness testimony in court or tribunals?',
      answer: 'Yes, Dr. Syed is highly experienced in providing robust and clear expert witness testimony in both court and tribunal settings, including Criminal, Family, Immigration, and Civil courts, as well as Mental Health Review Tribunals and Parole Board Hearings. His testimony is always impartial and grounded in his expert report findings.'
    }, {
      question: "What is Dr. Syed's approach to ensuring impartiality in his reports?",
      answer: "Dr. Syed adheres strictly to the expert's overriding duty to the court and the principles of the Civil Procedure Rules (CPR Part 35), Family Procedure Rules (FPR Part 25), and Criminal Procedure Rules (CrPR Part 19). All assessments are conducted objectively, and reports are evidence-based, impartial, and designed to assist the court, regardless of the instructing party."
    }]
  }, {
    id: 'assessments',
    title: 'Assessment Process & Accessibility',
    items: [{
      question: 'How are assessments conducted – remotely or in person?',
      answer: 'Dr. Syed offers excellent flexibility. Assessments can be conducted securely via remote video platforms (Zoom, Teams) across all regions of the UK. For in-person assessments, arrangements can be made in the West Midlands (Rugby base), East Midlands, and Greater London. Prison, care home visits, and court attendance are also available upon arrangement.'
    }, {
      question: 'Which mental health conditions does Dr. Syed assess?',
      answer: 'Dr. Syed assesses a comprehensive range of mental health conditions relevant to medico-legal contexts, including but not limited to: Major Depressive Disorder, Bipolar Affective Disorder, various Anxiety Disorders (GAD, Panic, Phobias), PTSD and Complex PTSD (including survivors of torture/modern slavery), Psychotic Disorders (e.g., Schizophrenia), OCD, ADHD, ASD, Personality Disorders, Substance Misuse, Neurocognitive Disorders (e.g., dementia), and more.'
    }, {
      question: 'Does Dr. Syed assess capacity for specific decisions (e.g., testamentary, DoLS, litigation)?',
      answer: 'Yes, Dr. Syed specialises in various mental capacity assessments under the Mental Capacity Act 2005, including capacity to litigate, testamentary capacity, capacity regarding property and affairs (COP3), capacity for residence and care decisions (including DoLS), and capacity for medical treatment decisions.'
    }]
  }, {
    id: 'fees',
    title: 'Fees & Billing',
    items: [{
      question: "How are Dr. Syed's fees structured?",
      answer: 'Fees for medico-legal assessments and expert witness services are typically based on the complexity of the case, the volume of documentation to be reviewed, the type of assessment required, and the estimated time involved. Detailed fee schedules and terms will be provided upon initial enquiry and prior to formal instruction. Please contact us for a tailored quotation.'
    }]
  }, {
    id: 'professional',
    title: 'Professional Standing',
    items: [{
      question: "What are Dr. Syed's professional qualifications and memberships?",
      answer: 'Dr. Syed holds an MBBS, MBA, and is currently pursuing an MSc in Clinical Psychiatry. He is a Member of the Royal College of Psychiatrists (MRCPsych), a Certified MedCo Medical Expert, and a Certified Expert Witness (Bond Solon and SEAK-trained). He is GMC-registered and maintains active memberships with professional bodies like the British Pakistani Psychiatrists Association (BPPA) and Expert Witness directories. Full details are available on the "About Dr. Imtiaz Syed" page.'
    }, {
      question: 'What languages does Dr. Syed speak?',
      answer: 'Dr. Syed is fluent in English and Urdu, and conversational in Hindi and Punjabi, which can facilitate communication during assessments where appropriate.'
    }]
  }];
  return <main className="min-h-screen bg-white" data-id="element-599" data-mp="35ygcns0pxkro">
      <SEO title="Psychiatric Expert Witness FAQs | Medicolegal Psychiatry" description="How to instruct, what a report contains, turnaround times and CPR Part 35 compliance, answered for instructing solicitors." path="/faq" keywords={['psychiatric expert witness FAQs', 'psychiatric report turnaround time', 'how to instruct an expert witness']} imageAlt="Frequently asked questions about psychiatric expert witness instructions" schema={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqCategories.flatMap(category => category.items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      })))
    }} data-id="element-600" data-mp="3jwxu58016115" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 pt-24 md:pt-28" data-id="element-601" data-mp="1boj7r959ed8z">
        <div className="container mx-auto px-4" data-id="element-602" data-mp="1bm6s52mz4ez3">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-center" data-id="element-603" data-mp="1gmb0u6hn45er">
            Frequently Asked Questions – Medico-Legal Psychiatry
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-604" data-mp="293dptruobtfv">
            Here you'll find answers to common questions regarding Dr. Imtiaz
            Syed's medico-legal psychiatric assessments and expert witness
            services.
          </p>
        </div>
      </section>
      {/* FAQ Content */}
      <section className="py-16" data-id="element-605" data-mp="8klhfyhc8hw">
        <div className="container mx-auto px-4" data-id="element-606" data-mp="9lvri7o8qyk">
          <div className="max-w-3xl mx-auto" data-id="element-607" data-mp="3rfnduaq20ktp">
            <p className="text-slate-dark text-lg mb-8" data-id="element-608" data-mp="3rgyew2s47eh4">
              Our aim is to provide transparent and helpful information to legal
              professionals, ensuring a smooth and efficient process.
            </p>
            <div className="space-y-6 mb-12" data-id="element-609" data-mp="nwk8c4so7er7">
              {faqCategories.map(category => <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-610" data-mp="j1oh36v9cvf5">
                  <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openItem === category.id ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleItem(category.id)} data-id="element-611" data-mp="33qub9vexfeq4">
                    <h2 className="text-xl font-serif font-bold" data-id="element-612" data-mp="2tlcist4s932h">
                      {category.title}
                    </h2>
                    {openItem === category.id ? <ChevronUp className="h-5 w-5" data-id="element-613" data-mp="33bfckvjqal1v" /> : <ChevronDown className="h-5 w-5" data-id="element-614" data-mp="33didiamf5yld" />}
                  </button>
                  {openItem === category.id && <div className="p-6 bg-white" data-id="element-615" data-mp="3cbc372pohtzk">
                      <div className="space-y-6" data-id="element-616" data-mp="3hbgod61j7cw4">
                        {category.items.map((item, index) => <div key={index} data-id="element-617" data-mp="3ma9v8fmbc5nb" data-mpi={index}>
                            <h3 className="text-lg font-bold text-primary mb-2" data-id="element-618" data-mp="3ma1f8euw8k7w">
                              {item.question}
                            </h3>
                            <p className="text-slate-dark" data-id="element-619" data-mp="1rw0h5w76418v">{item.answer}</p>
                          </div>)}
                      </div>
                    </div>}
                </div>)}
            </div>
            {/* Cancellation Policy */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-620" data-mp="24pziskxs9ujf">
              <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-621" data-mp="24su1a6x8l5a7">
                Cancellation Policy
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-622" data-mp="2epgoh2d1c8fe">
                This policy is designed to ensure the efficient management of
                our professional commitments and to provide clear guidelines for
                all parties.
              </p>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-623" data-mp="2oguhcrahe9zk">
                Standard Appointments and Assessments
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-624" data-mp="2xfy2e0t7kzgp">
                Due to the significant time allocated for each assessment,
                including preparation and report writing, our cancellation
                policy is strictly enforced to maintain the quality of service
                for all clients.
              </p>
              <ul className="space-y-2 mb-4 text-slate-dark" data-id="element-625" data-mp="37hijj3n0nnv0">
                <li className="flex items-start" data-id="element-626" data-mp="32jh2spd2c0mp">
                  <span className="text-secondary font-bold mr-2" data-id="element-627" data-mp="32m36gdwu1vzn">•</span>
                  <span data-id="element-628" data-mp="2yjqc0lnjdki6">
                    <strong data-id="element-629" data-mp="2ykhz1832i9wu">More than 72 hours' notice:</strong> There will be
                    no charge for appointments cancelled with more than 72
                    hours' notice.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-630" data-mp="3limmln620k5p">
                  <span className="text-secondary font-bold mr-2" data-id="element-631" data-mp="3lhbhnk0vh6d0">•</span>
                  <span data-id="element-632" data-mp="3hg82kzqnfecu">
                    <strong data-id="element-633" data-mp="3hgri69sttqnp">Less than 72 hours' notice:</strong> The instructing
                    party will be charged for all preparation work already
                    undertaken at our Hourly Rate, plus any incurred travel time
                    and expenses.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-634" data-mp="27czvx0dddx66">
                  <span className="text-secondary font-bold mr-2" data-id="element-635" data-mp="2ca9jsbl420rw">•</span>
                  <span data-id="element-636" data-mp="1xlddaiypcvte">
                    <strong data-id="element-637" data-mp="1xlvfrxbyk69r">Less than 24 hours' notice or a "no-show":</strong>{' '}
                    The full fee for the scheduled assessment may be charged, as
                    this time cannot be reallocated to other clients.
                  </span>
                </li>
              </ul>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-638" data-mp="1tjs355djyvmw">
                Court and Tribunal Attendance
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-639" data-mp="1ensr5jnbobch">
                The cancellation of court or tribunal attendance with short
                notice can result in substantial costs and is subject to
                separate terms.
              </p>
              <ul className="space-y-2 mb-4 text-slate-dark" data-id="element-640" data-mp="3dzvg39eptxxj">
                <li className="flex items-start" data-id="element-641" data-mp="3dx97r7k03ee9">
                  <span className="text-secondary font-bold mr-2" data-id="element-642" data-mp="12b8iqbg8x062">•</span>
                  <span data-id="element-643" data-mp="xd6xpkz9eqry">
                    <strong data-id="element-644" data-mp="xdotyhlx94zv">More than 7 working days' notice:</strong> There
                    will be no charge for the attendance fee. Any preparation
                    work already completed will be billed at the Hourly Rate.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-645" data-mp="1l2812tt8jbqd">
                  <span className="text-secondary font-bold mr-2" data-id="element-646" data-mp="1l0ww4qo1zxxo">•</span>
                  <span data-id="element-647" data-mp="1gzuuy4aktbt2">
                    <strong data-id="element-648" data-mp="1h0d2vy6lpegt">Less than 7 working days' notice:</strong> The full
                    fee for court attendance will be charged, in addition to all
                    preparation work, travel time, and expenses.
                  </span>
                </li>
              </ul>
              <h3 className="text-lg font-bold text-primary mb-2" data-id="element-649" data-mp="nft2sx97n8f">
                How to Cancel
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-650" data-mp="3rus934mehumr">
                All cancellations or requests to reschedule must be made in
                writing via email to enquiries@medicolegal-psychiatry.co.uk. The
                cancellation will be deemed effective from the time and date of
                receipt of the email.
              </p>
              <p className="text-slate-dark" data-id="element-651" data-mp="c1pxshq2bxcn">
                We understand that unforeseen circumstances can arise and will
                consider exceptions on a case-by-case basis.
              </p>
            </div>
            <div className="text-center" data-id="element-652" data-mp="3fjrk1vwa1amc">
              <p className="text-slate-dark mb-4" data-id="element-653" data-mp="3fglvqivh2cro">
                Still have questions? We're here to help.
              </p>
              <Button variant="primary" size="lg" href="/contact" data-id="element-654" data-mp="3p89gy21fqxkp">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}